import { Store, select } from '@ngrx/store';

import { ManageUserAccessState } from './manage-user-access.reducer';
import {
  GetAllUsers,
  GetPendingUsers,
  GetApprovedUsers,
  SelectAndEditUser,
  ViewAccessForUser,
  ViewAccessForUserReset,
  UserResetPassword,
  UserResetPasswordReset,
  UserRemoveAccess,
  UserRemoveAccessReset,
  Reset,
  AddUsersAndStartAssignment,
  SetAssignmentMethodAndAssign,
  GetUserTinEntitlements,
  TriggerUserScaffolding,
  AddNewUsers,
  GetEntitlements,
  GetJobRoles,
  UpdateEditedUsersBulkTins,
  UpdateEditedUsersBulkEntitlements,
  UnSelectUserToEdit,
  ApplyEntitlementsByJobRole,
  RevertEntitlementsToExisting,
  UpdateEditedUserIndividual,
  BulkEditApplyChanges,
  ViewAndEditUsers,
  SelectUserToEdit,
  SelectAllUsersToEdit,
  UnSelectAllUsersToEdit,
  PageOverivewFilterGroupChange,
  PageOverviewFilterTextChange,
  UsersSubmit,
  BackToAssignAccess,
  SelectUserToPrintEmail,
  UnSelectUserToPrintEmail,
  SelectAllUsersToPrintEmail,
  LoadUsersFromDashboard,
  GetAllTins,
  GetUserTinLOB,
  UpdateUserTinLOB,
} from './manage-user-access.actions';

import { manageAccessQuery } from './manage-user-access.selectors';
import { Injectable } from '@angular/core';
import {
  User,
  EntitlementRole,
  TinAccess,
  EditedUser,
} from '../manage-access.model';
import {
  FILTER_GROUP,
  PAGE_STEPS,
  PAGE_STEPS_WITHOUT_PREF_LOB,
  PAGE_STEPS_WITH_ONE_CONFIRMATION,
} from '../manage-access.constant';
import { TinLob } from '@cigna/chcp/shared/util';
import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { FeatureTogglesFacade } from '@cigna/features';
import { combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { LOB_DENTAL, LOB_OHP } from '@cigna/chcp/auth/util';

@Injectable({
  providedIn: 'root',
})
export class ManageUserAccessFacade {
  constructor(
    private store$: Store<ManageUserAccessState>,
    private authFacade: AuthFacade,
    private featureTogglesFacade: FeatureTogglesFacade,
  ) {}
  allUsers$ = this.store$.pipe(select(manageAccessQuery.getAllUsers));
  allTins$ = this.store$.pipe(select(manageAccessQuery.getAllTins));
  allEntitlements$ = this.store$.pipe(
    select(manageAccessQuery.getEntitlements),
  );
  allJobRoles$ = this.store$.pipe(select(manageAccessQuery.getJobRoles));
  eligibleJobRoles$ = this.store$.pipe(
    select(manageAccessQuery.getEligibleJobRoles),
  );
  overviewUsers$ = this.store$.pipe(
    select(manageAccessQuery.getPageOverviewUsers),
  );
  userTableView$ = this.store$.pipe(select(manageAccessQuery.getUserTableView));

  resetPasswordResponse$ = this.store$.pipe(
    select(manageAccessQuery.getResetPasswordResponse),
  );
  viewingUser$ = this.store$.pipe(select(manageAccessQuery.getViewingUser));
  resetPasswordView$ = this.store$.pipe(
    select(manageAccessQuery.getResetPasswordView),
  );
  viewAccessView$ = this.store$.pipe(
    select(manageAccessQuery.getViewAccessView),
  );
  removeAccessView$ = this.store$.pipe(
    select(manageAccessQuery.getRemoveAccessView),
  );
  overviewSelectedFilterGroup$ = this.store$.pipe(
    select(manageAccessQuery.getPageOverviewFilterGroup),
  );
  overviewFilterText$ = this.store$.pipe(
    select(manageAccessQuery.getPageOverviewFilterText),
  );
  selectedUsers$ = this.store$.pipe(
    select(manageAccessQuery.getSelectedUsersToEdit),
  );
  selectedUserType$ = this.store$.pipe(
    select(manageAccessQuery.getSelectedUserType),
  );
  selectedUsersLength$ = this.store$.pipe(
    select(manageAccessQuery.getSelectedUsersLength),
  );
  selectedUsersEntitlementsLoaded$ = this.store$.pipe(
    select(manageAccessQuery.getSelectedUsersEntitlementsLoaded),
  );
  hiddenSteps$ = this.store$.pipe(select(manageAccessQuery.getHiddenSteps));
  assignmentMethod$ = this.store$.pipe(
    select(manageAccessQuery.getAssignmentMethod),
  );
  bulkEditSelectedEntitlements$ = this.store$.pipe(
    select(manageAccessQuery.getBulkEditSelectedEntitlements),
  );
  bulkEditSelectedEntitlementsValid$ = this.store$.pipe(
    select(manageAccessQuery.getBulkEditSelectedEntitlementsValid),
  );
  individualSelectedEntitlementsValid$ = this.store$.pipe(
    select(manageAccessQuery.getIndividualSelectedEntitlementsValid),
  );
  bulkEditSelectedTins$ = this.store$.pipe(
    select(manageAccessQuery.getBulkEditSelectedTins),
  );
  bulkEditSelectedTinsValid$ = this.store$.pipe(
    select(manageAccessQuery.getBulkEditSelectedTinsValid),
  );
  isSubmitting$ = this.store$.pipe(select(manageAccessQuery.isSubmitting));
  submitError$ = this.store$.pipe(select(manageAccessQuery.getSubmitError));
  isVendorSuccess$ = this.store$.pipe(
    select(manageAccessQuery.isVendorSuccess),
  );
  selectedEmailPrintusers$ = this.store$.pipe(
    select(manageAccessQuery.getSelectedPrintEmailUsers),
  );
  allTinsLoaded$ = this.store$.pipe(select(manageAccessQuery.allTinsLoaded));
  userTinLobLoading$ = this.store$.pipe(
    select(manageAccessQuery.userTinLobLoading),
  );
  updateUserTinSuccess$ = this.store$.pipe(
    select(manageAccessQuery.updateUserTinSuccess),
  );
  userTinsWithLob$ = this.store$.pipe(
    select(manageAccessQuery.userTinsWithLob),
  );
  preferredLobOptions$ = this.store$.pipe(
    select(manageAccessQuery.preferredLobOptions),
  );
  steps$ = combineLatest([
    this.authFacade.lob$,
    this.featureTogglesFacade.featuresEnabled(['enablePreferredLOB']),
    this.featureTogglesFacade.featuresEnabled(['manageUserAccessNewExp']),
    this.selectedUserType$,
  ]).pipe(
    map(([lob, enablePrefLob, hasManageUserAccessNewExp, userType]) =>
      ![LOB_DENTAL, LOB_OHP].includes(lob || '') && enablePrefLob
        ? userType === 'new' || !hasManageUserAccessNewExp
          ? PAGE_STEPS
          : PAGE_STEPS_WITH_ONE_CONFIRMATION
        : PAGE_STEPS_WITHOUT_PREF_LOB,
    ),
  );

  reset(): void {
    this.store$.dispatch(new Reset());
  }
  getAllUsers(): void {
    this.store$.dispatch(new GetAllUsers());
  }
  getPendingUsers(): void {
    this.store$.dispatch(new GetPendingUsers());
  }
  getApprovedUsers(): void {
    this.store$.dispatch(new GetApprovedUsers());
  }
  addNewUsersTrigger(): void {
    this.store$.dispatch(new AddNewUsers());
  }
  getAllWamTins(): void {
    this.store$.dispatch(new GetAllTins());
  }
  addUsersAndStartAssignment(users: any): void {
    this.store$.dispatch(new AddUsersAndStartAssignment(users));
  }
  selectAndEditUser(user: User): void {
    this.store$.dispatch(new SelectAndEditUser(user));
  }
  unselectUserToEdit(user: User): void {
    this.store$.dispatch(new UnSelectUserToEdit(user));
  }
  viewAndEditUsers(): void {
    this.store$.dispatch(new ViewAndEditUsers());
  }
  selectUser(user: User): void {
    this.store$.dispatch(new SelectUserToEdit(user));
  }
  unSelectUser(user: User): void {
    this.store$.dispatch(new UnSelectUserToEdit(user));
  }
  selectAllUsersToEdit(): void {
    this.store$.dispatch(new SelectAllUsersToEdit());
  }
  unSelectAllUsersToEdit(): void {
    this.store$.dispatch(new UnSelectAllUsersToEdit());
  }
  filterStateChange(text: FILTER_GROUP): void {
    this.store$.dispatch(new PageOverivewFilterGroupChange(text));
  }
  filterTextChange(text: string): void {
    this.store$.dispatch(new PageOverviewFilterTextChange(text));
  }
  viewAccessForUser(user: User): void {
    this.store$.dispatch(new ViewAccessForUser(user));
  }
  viewAccessForUserReset(): void {
    this.store$.dispatch(new ViewAccessForUserReset());
  }
  resetUserPassword(userId: string): void {
    this.store$.dispatch(new UserResetPassword(userId));
  }
  resetUserPasswordView(): void {
    this.store$.dispatch(new UserResetPasswordReset());
  }
  removeAccess(data: { userId: string; tins: TinLob[] }): void {
    this.store$.dispatch(new UserRemoveAccess(data));
  }
  resetRemoveAccessView(): void {
    this.store$.dispatch(new UserRemoveAccessReset());
  }
  setAssignMethodAndProceed(assignmentType: string): void {
    this.store$.dispatch(new SetAssignmentMethodAndAssign(assignmentType));
  }
  triggerUserScaffolding(): void {
    this.store$.dispatch(new TriggerUserScaffolding());
  }
  loadUsersFromDashboard(userIds: string[]): void {
    this.store$.dispatch(new LoadUsersFromDashboard(userIds));
  }
  loadUsersEntitlements(): void {
    this.store$.dispatch(new GetUserTinEntitlements());
  }
  updateEditedUsersBulkTins(tins: TinLob[]): void {
    this.store$.dispatch(new UpdateEditedUsersBulkTins(tins));
  }
  updateEditedUsersBulkEntitlements(entitlements: EntitlementRole[]): void {
    this.store$.dispatch(new UpdateEditedUsersBulkEntitlements(entitlements));
  }
  bulkEditApplyChanges(): void {
    this.store$.dispatch(new BulkEditApplyChanges());
  }
  applyEntitlementsByJobRole(data: { tin: TinAccess; user: EditedUser }): void {
    this.store$.dispatch(new ApplyEntitlementsByJobRole(data));
  }
  revertEntitlementsToExisting(data: {
    tin: TinAccess;
    user: EditedUser;
  }): void {
    this.store$.dispatch(new RevertEntitlementsToExisting(data));
  }
  updatedEditedUserIndividual(data: {
    entitlements: EntitlementRole[];
    tin: TinAccess;
    user: EditedUser;
  }): void {
    this.store$.dispatch(new UpdateEditedUserIndividual(data));
  }
  backToAssign(): void {
    this.store$.dispatch(new BackToAssignAccess());
  }

  getUserTinLOB(userId: string): void {
    this.store$.dispatch(new GetUserTinLOB(userId));
  }

  updateUserTinLOB(userId: string, lobPref: string): void {
    this.store$.dispatch(new UpdateUserTinLOB({ userId, lobPref }));
  }

  submitEntitlements(): void {
    this.store$.dispatch(new UsersSubmit());
  }

  selectUserToPrintEmail(user: EditedUser): void {
    this.store$.dispatch(new SelectUserToPrintEmail(user));
  }
  unSelectUserToPrintEmail(user: EditedUser): void {
    this.store$.dispatch(new UnSelectUserToPrintEmail(user));
  }
  selectAllUsersToPrintEmail(all: boolean): void {
    this.store$.dispatch(new SelectAllUsersToPrintEmail(all));
  }

  loadDelegationData(): void {
    this.store$.dispatch(new GetJobRoles());
    this.store$.dispatch(new GetEntitlements());
  }
}
